import { ArrowsUpDownIcon } from '@heroicons/react/16/solid';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import { type ReactNode } from 'react';

import { cn } from '../../../lib/utils';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../ui/catalyst/dropdown';
import { ColumnFilterTrigger } from './ColumnFilterTrigger';
import { SortOption } from './types';

interface SortOnlyDropdownProps {
  /** Column header text */
  header: string | ReactNode;
  /** Sort options to display */
  sortOptions: SortOption[];
  /** Current sort field (to highlight active option) */
  sortField?: string | null;
  /** Current sort direction */
  sortDirection?: 'asc' | 'desc' | null;
  /** Sort change handler */
  onSortChange: (direction: 'asc' | 'desc', sortKey: string) => void;
}

/**
 * Sort-only dropdown for columns with multiple sort options but no filter.
 * Shows sort options directly (flattened, not in expandable section).
 */
export const SortOnlyDropdown = ({
  header,
  sortOptions,
  sortField,
  sortDirection,
  onSortChange,
}: SortOnlyDropdownProps) => {
  const isActive = sortOptions.some((opt) => opt.key === sortField);

  // Flatten sort options into a single list of items with dividers
  const items: Array<{
    type: 'divider' | 'item';
    key: string;
    option?: SortOption;
    direction?: 'asc' | 'desc';
  }> = [];

  sortOptions.forEach((option, index) => {
    // Add divider before each option group (except first)
    if (index > 0) {
      items.push({ type: 'divider', key: `divider-${option.key}` });
    }
    // Add ascending option
    items.push({
      type: 'item',
      key: `${option.key}-asc`,
      option,
      direction: 'asc',
    });
    // Add descending option
    items.push({
      type: 'item',
      key: `${option.key}-desc`,
      option,
      direction: 'desc',
    });
  });

  return (
    <Dropdown>
      <DropdownButton as={ColumnFilterTrigger}>
        <div className="flex items-center gap-1">
          <span className="font-semibold text-[12px] leading-6 text-black dark:text-white">
            {header}
          </span>
          {isActive && sortDirection ? (
            <span className="text-blue-600 dark:text-blue-400 text-xs">
              {sortDirection === 'asc' ? '↑' : '↓'}
            </span>
          ) : (
            <ArrowsUpDownIcon
              className="h-3 w-3 text-zinc-400 dark:text-zinc-500"
              aria-label="Sortable column"
            />
          )}
        </div>
      </DropdownButton>

      <DropdownMenu className="min-w-[200px]" anchor="bottom start">
        {items.map((item) => {
          if (item.type === 'divider') {
            return <DropdownDivider key={item.key} />;
          }

          const { option, direction } = item;
          if (!option || !direction) return null;

          const isSelected =
            sortField === option.key && sortDirection === direction;
          const label =
            direction === 'asc' ? option.ascLabel : option.descLabel;
          const Icon = direction === 'asc' ? ArrowUpIcon : ArrowDownIcon;

          return (
            <DropdownItem
              key={item.key}
              onClick={() => onSortChange(direction, option.key)}
              className={cn(
                isSelected ? 'text-blue-600 dark:text-blue-400' : '',
              )}
            >
              <Icon data-slot="icon" />
              <DropdownLabel>{label}</DropdownLabel>
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};
